/**
 * useEkipAssign.ts — thêm/gỡ người trong ekip của đơn.
 *
 * Bọc useAddEkipMember/useRemoveEkipMember (features/orders/api.ts) với cổng
 * quyền + toast.
 *
 * ╔════════════════════════════════════════════════════════════════════════╗
 * ║ GỬI DELTA, KHÔNG GỬI CẢ MẢNG. Quyết định 0121.                          ║
 * ╚════════════════════════════════════════════════════════════════════════╝
 *
 * Trước 11/09/2026, mỗi lần sửa ekip màn hình dựng lại CẢ mảng từ `members` nó
 * đang giữ rồi PUT /orders/:id. Nhưng `members` đó đến từ cache React Query có
 * staleTime 30 giây — nên trong 30 giây đó, nếu ai khác (hoặc máy chia đơn)
 * vừa thêm một người, thao tác thêm/gỡ bất kỳ ai sẽ ghi đè trắng người vừa
 * được thêm. Không lỗi, không cảnh báo, chỉ mất.
 *
 * Thao tác THẬT của người dùng vốn đã là delta: bấm "＋ Thêm người", bấm "×"
 * trên một chip. Chính bước dịch ngược delta → snapshot sinh ra lỗi. Nay gửi
 * thẳng delta, nên không có gì để mà cũ: hai người ngồi hai máy cùng thêm hai
 * người khác nhau thì cả hai cùng vào.
 *
 * 6 cột phẳng cũ (photographerId/Name, makeupId/Name, retoucher/Name) đã bỏ
 * khỏi OpenAPI (migration 0194 đã DROP ở DB).
 */

import { useCallback } from "react";
import { toast } from "sonner";
import { useAddEkipMember, useRemoveEkipMember, type EkipRole } from "@/features/orders/api";
import { fetchPrecheck } from "@/features/order-dispatch/api";
import type { DispatchSlotKind } from "@/features/order-dispatch/schemas";
import type { OverrideReasonCode } from "@/features/order-dispatch/overrideReasons";
import type { EkipPickExtra } from "./components/EkipPicker";
import { useAuthV2 as useAuth } from "@/lib/authv2/store";
import { canAssignPhoto, canAssignMakeup, canAssignRetouch } from "./permissions";

export type EkipField = "photographer" | "makeup" | "retouch";

export type EkipMember = { id: string; name: string };

/**
 * Vai nào của đơn khớp với một suất của máy chia đơn Profile. Hậu kỳ cố ý
 * vắng: máy không chia suất retouch, hỏi precheck cho nó là hỏi một câu backend
 * không trả lời được.
 */
const SLOT_KIND: Partial<Record<EkipField, DispatchSlotKind>> = {
  photographer: "photo",
  makeup: "makeup",
};

/**
 * Cảnh báo trùng lịch TRƯỚC khi gán tay ở màn lịch (quyết định 0077).
 *
 * Ba luật, đừng đổi:
 *   1. CẢNH BÁO, KHÔNG CHẶN — người đứng quầy biết thực tế hơn máy, họ bấm
 *      "OK" là đi tiếp.
 *   2. Precheck hỏng (mất mạng, 404, tính năng chưa bật) thì BỎ QUA và gán
 *      như cũ. Đường gán tay này chạy từ lâu trước khi có máy chia đơn —
 *      để nó chết theo một cảnh báo phụ là hỏng cả màn lịch.
 *   3. Chỉ hỏi khi backend nói `busy === true`. Hai cảnh báo mềm còn lại
 *      (ngoài ca, quá trần) thuộc về cửa Override của bảng suất, không phải
 *      đường gán tay cũ.
 *
 * Trả `false` khi người dùng bấm Huỷ — caller phải dừng, không PUT.
 *
 * Chứng chỉ chuyên ngành (QĐ 0101): precheck còn trả `certMissing`. Chỉ gộp
 * vào câu hỏi khi ô chọn CHƯA xử lý (`certHandled=false`) — ô chọn đã nhắc/đã
 * lấy lý do rồi mà hỏi lần nữa là hỏi hai lần cùng một chuyện.
 */
async function confirmNoClash(
  orderId: string,
  field: EkipField,
  people: ReadonlyArray<{ id: string; name: string }>,
  certHandled: boolean,
): Promise<boolean> {
  const kind = SLOT_KIND[field];
  if (!kind) return true;
  const targets = people.filter((p) => p.id);
  if (targets.length === 0) return true;

  const checked = await Promise.all(
    targets.map(async (p) => {
      try {
        const r = await fetchPrecheck(orderId, kind, p.id);
        const notes: string[] = [];
        if (r.busy) notes.push("đã có đơn trùng khung giờ");
        if (!certHandled && r.certMissing) {
          notes.push(`chưa có chứng chỉ ${r.certSpecialty || "chuyên ngành"}`);
        }
        return notes.length ? `${p.name || p.id} ${notes.join(", ")}` : null;
      } catch {
        // Best-effort: không soi được thì im lặng cho qua (luật 2).
        return null;
      }
    }),
  );
  const clashing = checked.filter((n): n is string => n !== null);
  if (clashing.length === 0) return true;

  return window.confirm(`${clashing.join("; ")} — vẫn gán?`);
}

// Vai trên URL của hai cửa delta (POST/DELETE /orders/:id/ekip/:role/members).
const ROLE_PATH: Record<EkipField, EkipRole> = {
  photographer: "photographer",
  makeup: "makeup",
  retouch: "retouch",
};

const RBAC_LABEL: Record<EkipField, string> = {
  photographer: "Photographer",
  makeup: "Makeup",
  retouch: "Retouch",
};

export function useEkipAssign() {
  const me = useAuth((s) => s.currentStaff);
  const addMutation = useAddEkipMember();
  const removeMutation = useRemoveEkipMember();

  // Shared RBAC gate: returns true when the current user may assign `field`,
  // else toasts and returns false.
  const canAssign = useCallback(
    (field: EkipField): boolean => {
      const ok =
        field === "photographer"
          ? canAssignPhoto(me)
          : field === "makeup"
            ? canAssignMakeup(me)
            : canAssignRetouch(me);
      if (!ok) {
        toast.error(`Bạn không có quyền phân công ${RBAC_LABEL[field]}.`);
      }
      return ok;
    },
    [me],
  );

  // addMember — thêm ĐÚNG MỘT người vào một vai. Không gửi danh sách hiện có,
  // nên không ghi đè được ai.
  const addMember = useCallback(
    async (
      orderId: string,
      field: EkipField,
      person: EkipMember,
      extra?: EkipPickExtra,
    ): Promise<boolean> => {
      if (!canAssign(field)) return false;
      const certHandled = Boolean(extra?.cert || extra?.certReason);
      if (!(await confirmNoClash(orderId, field, [person], certHandled))) return false;
      try {
        await addMutation.mutateAsync({
          orderId,
          role: ROLE_PATH[field],
          staffId: person.id,
          staffName: person.name,
          // Lý do gán ngoại lệ chứng chỉ (chế độ CHẶN) — máy chủ ghi sổ kèm đơn.
          ...(extra?.certReason ? { certExceptionReason: extra.certReason } : {}),
        });
        toast.success(`Đã thêm ${person.name || person.id} vào ekip ${RBAC_LABEL[field]}`);
        return true;
      } catch (e) {
        toast.error(errMsg(e));
        return false;
      }
    },
    [canAssign, addMutation],
  );

  // removeMember — gỡ ĐÚNG MỘT người khỏi một vai. Người còn lại giữ nguyên.
  // Thợ chụp / trang điểm phải kèm lý do (QĐ 0424) — gọi qua useCrewRemoval
  // để hiện hộp chọn lý do; hậu kỳ gỡ thẳng.
  const removeMember = useCallback(
    async (
      orderId: string,
      field: EkipField,
      person: EkipMember,
      reason?: { code: OverrideReasonCode; note?: string },
    ): Promise<boolean> => {
      if (!canAssign(field)) return false;
      try {
        await removeMutation.mutateAsync({
          orderId,
          role: ROLE_PATH[field],
          staffId: person.id,
          ...(reason ? { reasonCode: reason.code } : {}),
          ...(reason?.note?.trim() ? { reasonNote: reason.note.trim() } : {}),
        });
        toast.success(`Đã gỡ ${person.name || person.id} khỏi ekip ${RBAC_LABEL[field]}`);
        return true;
      } catch (e) {
        toast.error(errMsg(e));
        return false;
      }
    },
    [canAssign, removeMutation],
  );

  return {
    addMember,
    removeMember,
    isPending: addMutation.isPending || removeMutation.isPending,
  };
}

function errMsg(e: unknown): string {
  return e instanceof Error && e.message ? `Phân công thất bại: ${e.message}` : "Phân công thất bại";
}
